import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ProfileWizardProvider } from "@/contexts/ProfileWizardContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/providers/AuthProvider";
import { CookieConsentBanner } from "@/components/cookies/CookieConsentBanner";
import { BrandLayout } from "@/components/brand/BrandLayout";
import { MarketingLayout } from "@/components/layouts";

// Critical paths - keep eager
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy load all pages for better initial load performance
const Home = lazy(() => import("./pages/Home"));
const HomeV2 = lazy(() => import("./pages/HomeV2"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Earn = lazy(() => import("./pages/Earn"));
const Protect = lazy(() => import("./pages/Protect"));
const Investors = lazy(() => import("./pages/Investors"));
const PressRelease = lazy(() => import("./pages/PressRelease"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Predictions = lazy(() => import("./pages/Predictions"));
const CampaignLanding = lazy(() => import("./pages/CampaignLanding"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const DocuSignCallback = lazy(() => import("./pages/DocuSignCallback"));
const BreachNews = lazy(() => import("./pages/BreachNews"));
const BreachArticle = lazy(() => import("./pages/BreachArticle"));
const BrandSignup = lazy(() => import("./pages/BrandSignup"));
const BrandLogin = lazy(() => import("./pages/BrandLogin"));
const BrandDashboard = lazy(() => import("./pages/BrandDashboard"));
const BrandForgotPassword = lazy(() => import("./pages/BrandForgotPassword"));
const BrandResetPassword = lazy(() => import("./pages/BrandResetPassword"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminForgotPassword = lazy(() => import("./pages/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const B2B = lazy(() => import("./pages/B2B"));
const Webinars = lazy(() => import("./pages/Webinars"));
const Sweepstakes = lazy(() => import("./pages/Sweepstakes"));
const WebinarDetail = lazy(() => import("./pages/WebinarDetail"));
const Partners = lazy(() => import("./pages/Partners"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const ContactSales = lazy(() => import("./pages/ContactSales"));
const Careers = lazy(() => import("./pages/Careers"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const LegacyBlogArticleRedirect = lazy(() =>
  import("./pages/LegacyBlogRedirects").then((m) => ({
    default: m.LegacyBlogArticleRedirect,
  })),
);

// Legal pages
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const DoNotSellMyData = lazy(() => import("./pages/legal/DoNotSellMyData"));
const ProtectTerms = lazy(() => import("./pages/legal/ProtectTerms"));
const NoticeOfIncentive = lazy(() => import("./pages/legal/NoticeOfIncentive"));
const ProtectPrivacyPolicy = lazy(() => import("./pages/legal/ProtectPrivacyPolicy"));
const ProtectTermsAndConditions = lazy(() => import("./pages/legal/ProtectTermsAndConditions"));
const DataBrokerPrivacyNotice = lazy(() => import("./pages/legal/DataBrokerPrivacyNotice"));
const DeleteMyAccount = lazy(() => import("./pages/legal/DeleteMyAccount"));
const Optout = lazy(() => import("./pages/Optout"));
const DownloadApp = lazy(() => import("./pages/DownloadApp"));
const ReklaimInvest = lazy(() => import("./pages/ReklaimInvest"));
const Enlaces = lazy(() => import("./pages/Enlaces"));
const CaliforniaConsumerRightsMetrics = lazy(
  () => import("./pages/CaliforniaConsumerRightsMetrics"),
);
const GeoBlocked = lazy(() => import("./pages/GeoBlocked"));

// Intel (internal staff intelligence)
const IntelLayout = lazy(() => import("./components/intel/IntelLayout").then(m => ({ default: m.IntelLayout })));
const IntelGuard = lazy(() => import("./components/intel/IntelGuard").then(m => ({ default: m.IntelGuard })));
const IntelHome = lazy(() => import("./pages/intel/IntelHome"));
const IntelSettings = lazy(() => import("./pages/intel/IntelSettings"));
const IntelPlaceholder = lazy(() => import("./pages/intel/IntelPlaceholder"));
const IntelDashboards = lazy(() => import("./pages/intel/IntelDashboards"));

// Landing pages (paid acquisition)
const ExtraCashLanding = lazy(() => import("./pages/landing/ExtraCashLanding"));
const WelcomeBackLanding = lazy(() => import("./pages/landing/WelcomeBackLanding"));
const QuickQuizLanding = lazy(() => import("./pages/landing/QuickQuizLanding"));
const MegaJackpotLanding = lazy(() => import("./pages/landing/MegaJackpotLanding"));
const WarriorStyleLanding = lazy(() => import("./pages/landing/sweepstakes/WarriorStyleLanding"));
const BigTenStyleLanding = lazy(() => import("./pages/landing/sweepstakes/BigTenStyleLanding"));
const F1StyleLanding = lazy(() => import("./pages/landing/sweepstakes/F1StyleLanding"));
const SweepstakesStyleShowcase = lazy(() => import("./pages/landing/sweepstakes/SweepstakesStyleShowcase"));
const WarriorStyleLandingV2 = lazy(() => import("./pages/landing/sweepstakes/WarriorStyleLandingV2"));
const BigTenStyleLandingV2 = lazy(() => import("./pages/landing/sweepstakes/BigTenStyleLandingV2"));
const F1StyleLandingV2 = lazy(() => import("./pages/landing/sweepstakes/F1StyleLandingV2"));
const LiveWinnersPage = lazy(() => import("./pages/LiveWinnersPage"));
const LiveWinnersControlPage = lazy(() => import("./pages/LiveWinnersControlPage"));
const SignupRedirect = lazy(() => import("./pages/SignupRedirect"));
const AuthReturn = lazy(() => import("./pages/AuthReturn"));

// Optimized QueryClient with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute - reduce refetches
      gcTime: 1000 * 60 * 5, // 5 minutes garbage collection
      retry: 1, // Single retry on failure
      refetchOnWindowFocus: false, // Don't refetch on tab focus
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-muted-foreground">Loading...</span>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <ProfileWizardProvider>
              <ErrorBoundary>
                <Toaster />
                <Sonner />
                <ScrollToTop />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Marketing pages -- shared Navbar + Footer via MarketingLayout */}
                    <Route element={<MarketingLayout />}>
                      <Route path="/" element={<Home />} />
                      <Route path="/home-v2" element={<HomeV2 />} />
                      <Route path="/how-it-works" element={<HowItWorks />} />
                      <Route path="/earn" element={<Earn />} />
                      <Route path="/protect" element={<Protect />} />
                      <Route path="/b2b" element={<B2B />} />
                      <Route path="/investors" element={<Investors />} />
                      <Route path="/investors/press/:slug" element={<PressRelease />} />
                      <Route path="/webinars" element={<Webinars />} />
                      <Route path="/webinars/:slug" element={<WebinarDetail />} />
                      <Route path="/partners" element={<Partners />} />
                      <Route path="/contact-us" element={<ContactUs />} />
                      <Route path="/contact-sales" element={<ContactSales />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/category/*" element={<Navigate to="/blog" replace />} />
                      <Route path="/blog/tag/*" element={<Navigate to="/blog" replace />} />
                      <Route path="/blog/:slug" element={<BlogArticle />} />
                      {/* Legacy consumer / partner blog URLs → unified /blog */}
                      <Route
                        path="/learn"
                        element={<Navigate to="/blog?audience=consumer" replace />}
                      />
                      <Route path="/learn/category/*" element={<Navigate to="/blog" replace />} />
                      <Route path="/learn/tag/*" element={<Navigate to="/blog" replace />} />
                      <Route path="/learn/:slug" element={<LegacyBlogArticleRedirect />} />
                      <Route
                        path="/partner-blog"
                        element={<Navigate to="/blog?audience=partner" replace />}
                      />
                      <Route path="/partner-blog/category/*" element={<Navigate to="/blog" replace />} />
                      <Route path="/partner-blog/tag/*" element={<Navigate to="/blog" replace />} />
                      <Route
                        path="/partner-blog/:slug"
                        element={<LegacyBlogArticleRedirect />}
                      />
                      <Route path="/predictions" element={<Predictions />} />
                      <Route path="/breaches" element={<BreachNews />} />
                      <Route path="/breaches/:slug" element={<BreachArticle />} />
                      <Route path="/breach-news" element={<Navigate to="/breaches" replace />} />
                      <Route path="/sweepstakes" element={<Sweepstakes />} />
                      <Route path="/help" element={<HelpCenter />} />
                      <Route path="/join/:campaign" element={<CampaignLanding />} />
                      <Route path="/download-app" element={<DownloadApp />} />
                      <Route path="/reklaim-invest" element={<ReklaimInvest />} />
                      <Route path="/optout" element={<Optout />} />
                      <Route path="/geo-blocked" element={<GeoBlocked />} />
                      <Route path="/campaign-landing" element={<CampaignLanding />} />

                      {/* Legacy redirects */}
                      <Route path="/signals" element={<Navigate to="/how-it-works" replace />} />
                      <Route path="/rewards" element={<Navigate to="/earn" replace />} />
                      <Route path="/shield" element={<Navigate to="/protect" replace />} />
                      <Route path="/home" element={<Navigate to="/" replace />} />
                      <Route path="/hnwi" element={<Navigate to="/protect" replace />} />
                      <Route path="/vip-program" element={<Navigate to="/how-it-works" replace />} />
                      <Route path="/webinars/tag/*" element={<Navigate to="/webinars" replace />} />
                      <Route path="/investors/press/tag/*" element={<Navigate to="/investors" replace />} />
                      <Route path="/careers/current-opportunities" element={<Navigate to="/careers" replace />} />
                      <Route path="/contacto" element={<Navigate to="/contact-us" replace />} />
                    </Route>

                    {/* Standalone chrome pages */}
                    <Route path="/enlaces" element={<Enlaces />} />
                    <Route
                      path="/california-consumer-rights-request-metrics-2025"
                      element={<CaliforniaConsumerRightsMetrics />}
                    />

                    {/* Legal pages -- self-contained LegalPage shell */}
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/cookies" element={<CookiePolicy />} />
                    <Route path="/do-not-sell" element={<DoNotSellMyData />} />
                    <Route path="/protect-terms" element={<ProtectTerms />} />
                    <Route path="/notice-of-incentive" element={<NoticeOfIncentive />} />
                    <Route path="/protect-privacy-policy" element={<ProtectPrivacyPolicy />} />
                    <Route path="/protect-terms-and-conditions" element={<ProtectTermsAndConditions />} />
                    <Route path="/delete-my-account" element={<DeleteMyAccount />} />
                    <Route path="/data-broker-privacy-notice" element={<DataBrokerPrivacyNotice />} />

                    {/* Auth routes */}
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/return" element={<AuthReturn />} />
                    <Route path="/signup" element={<SignupRedirect />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/onboarding" element={<Onboarding />} />

                    {/* Dashboard routes */}
                    <Route path="/dashboard/*" element={<Dashboard />} />

                    {/* DocuSign callback */}
                    <Route path="/docusign/callback" element={<DocuSignCallback />} />

                    <Route path="/email-lab" element={<Navigate to="/admin/email-lab" replace />} />
                    <Route path="/brand-lab" element={<Navigate to="/admin/brand-lab" replace />} />

                    {/* Intel — internal staff only */}
                    <Route path="/intel" element={<IntelGuard><IntelLayout /></IntelGuard>}>
                      <Route index element={<IntelHome />} />
                      <Route path="executive" element={<IntelGuard requireExecutive><IntelPlaceholder title="Executive Cockpit" description="North star, KPI scorecard, briefings, board pack." phase="Phase 3" /></IntelGuard>} />
                      <Route path="dashboards" element={<IntelDashboards />} />
                      <Route path="chat" element={<IntelPlaceholder title="Ask Intel" description="Chat with Reklaim's internal knowledge and live metrics." phase="Phase 4" />} />
                      <Route path="knowledge" element={<IntelPlaceholder title="Knowledge" description="Documents, decisions, retros — searchable with citations." phase="Phase 4" />} />
                      <Route path="sales" element={<IntelPlaceholder title="Sales Studio" description="Generate shareable text + image assets about our taxonomy and audience." phase="Phase 7" />} />
                      <Route path="risks" element={<IntelPlaceholder title="Risks" description="Open risks, owners and mitigations." phase="Phase 0" />} />
                      <Route path="decisions" element={<IntelPlaceholder title="Decisions" description="Append-only log of company decisions." phase="Phase 0" />} />
                      <Route path="settings" element={<IntelSettings />} />
                    </Route>

                    {/* Paid acquisition landing pages */}
                    <Route path="/lp/extra-cash" element={<ExtraCashLanding />} />
                    <Route path="/lp/welcome-back" element={<WelcomeBackLanding />} />
                    <Route path="/lp/quick-quiz" element={<QuickQuizLanding />} />
                    <Route path="/lp/mega-jackpot" element={<MegaJackpotLanding />} />
                    <Route path="/lp/mega-monthly-warrior" element={<WarriorStyleLanding />} />
                    <Route path="/lp/mega-monthly-bigten" element={<BigTenStyleLanding />} />
                    <Route path="/lp/mega-monthly-f1" element={<F1StyleLanding />} />
                    <Route path="/lp/sweepstakes-styles" element={<SweepstakesStyleShowcase />} />
                    <Route path="/lp/mega-monthly-warrior-v2" element={<WarriorStyleLandingV2 />} />
                    <Route path="/lp/mega-monthly-bigten-v2" element={<BigTenStyleLandingV2 />} />
                    <Route path="/lp/mega-monthly-f1-v2" element={<F1StyleLandingV2 />} />

                    {/* Ops broadcast — StreamYard graphic (no marketing chrome) */}
                    <Route path="/live-winners" element={<LiveWinnersPage />} />
                    <Route path="/live-winners/control" element={<LiveWinnersControlPage />} />

                    {/* Admin routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                    <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                    <Route path="/admin/*" element={<AdminDashboard />} />

                    {/* Brand routes */}
                    <Route element={<BrandLayout />}>
                      <Route path="/brand/signup" element={<BrandSignup />} />
                      <Route path="/brand/login" element={<BrandLogin />} />
                      <Route path="/brand/forgot-password" element={<BrandForgotPassword />} />
                      <Route path="/brand/reset-password" element={<BrandResetPassword />} />
                      <Route path="/brand/*" element={<BrandDashboard />} />
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <CookieConsentBanner hideOnPaths={["/live-winners"]} />
              </ErrorBoundary>
            </ProfileWizardProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
